import type { GameTile } from '../../lib/api';

export interface GameAvailability {
  playable: boolean;
  reason: string | null;
}

export function livePlayerCount(lobby: { players: Array<{ isSpectator?: boolean }> } | null): number {
  return lobby?.players.filter((p) => !p.isSpectator).length ?? 0;
}

export function gameAvailability(g: GameTile, playerCount: number): GameAvailability {
  if (playerCount < 2) return { playable: false, reason: 'Needs 2+ players' };
  const formats = g.playerFormats?.length
    ? g.playerFormats
    : [{ label: `${Math.max(2, g.minPlayers ?? 2)}-${g.maxPlayers ?? 4} players`, minPlayers: Math.max(2, g.minPlayers ?? 2), maxPlayers: g.maxPlayers ?? 4, countMode: 'range' as const }];
  const playable = formats.some((f) => {
    if (f.countMode === 'exact') return playerCount === f.minPlayers && playerCount === f.maxPlayers;
    return playerCount >= f.minPlayers && playerCount <= f.maxPlayers;
  });
  if (playable) return { playable: true, reason: null };
  const exact = formats.find((f) => f.countMode === 'exact' && f.minPlayers === f.maxPlayers);
  if (exact) return { playable: false, reason: `Needs ${exact.minPlayers} players - ${exact.label}` };
  const min = Math.min(...formats.map((f) => f.minPlayers));
  const max = Math.max(...formats.map((f) => f.maxPlayers));
  if (playerCount < min) return { playable: false, reason: `Needs ${min}+ players` };
  if (playerCount > max) return { playable: false, reason: `Works with up to ${max} players` };
  return { playable: false, reason: 'No format for this player count' };
}

export function playerReqLabel(g: GameTile): string {
  const formats = g.playerFormats ?? [];
  if (formats.length === 1) {
    const f = formats[0]!;
    if (f.countMode === 'exact' && f.minPlayers === f.maxPlayers) return `${f.minPlayers}p`;
    return `${f.minPlayers}-${f.maxPlayers}p`;
  }
  const min = Math.max(2, g.minPlayers ?? 2);
  const max = g.maxPlayers ?? 4;
  return min === max ? `${min}p` : `${min}-${max}p`;
}
